import { cs } from '../../../lib/currency';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Loader2, UsersRound, AlertCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../../services/api';
import { formatTime } from './utils';

/**
 * Admin/staff-only: reserve a small-group SLOT on this lane × date × time
 * using one of the BASELINE sessions already configured under
 * Coaching → Small Group Sessions (each session has its coach, max capacity
 * and pricing-per-hour-by-num-kids grid pre-set by admin).
 *
 * Flow:
 *   1. Fetch /api/small-group/sessions?active_only=true → user picks one.
 *   2. Preview the chosen session's coach + pricing grid.
 *   3. Pick a duration (defaults to the session's default).
 *   4. POST /api/small-group/slots with lane_id → backend auto-blocks
 *      the lane via create_lane_block_for_slot.
 *
 * No session creation happens here — the user must build a baseline session
 * first via the dedicated admin page.
 */
const DURATION_OPTIONS = [
  { value: '1', label: '1 hour' },
  { value: '2', label: '2 hours' },
  { value: '3', label: '3 hours' },
  { value: '4', label: '4 hours' },
];

const addHours = (hhmm, hours) => {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + Math.round(hours * 60);
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
};

export default function CreateSmallGroupModal({
  open,
  onOpenChange,
  laneId,
  laneName,
  date,
  startTime,
  onCreated,
}) {
  const [sessions, setSessions] = useState([]);
  const [coachPricing, setCoachPricing] = useState({}); // coach_id -> pricing grid
  const [loadingSessions, setLoadingSessions] = useState(false);

  const [sessionId, setSessionId] = useState('');
  const [durationHours, setDurationHours] = useState('1');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch baseline sessions + coach pricing whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingSessions(true);
      try {
        const [sRes, pRes] = await Promise.all([
          api.get('/small-group/sessions?active_only=true'),
          api.get('/small-group/pricing').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setSessions(sRes.data || []);
        const map = {};
        for (const p of (pRes.data || [])) {
          map[p.coach_id] = p.pricing_grid || [];
        }
        setCoachPricing(map);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load small group sessions');
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    })();
    // reset form
    setSessionId('');
    setDurationHours('1');
    setMaxCapacity('');
    return () => { cancelled = true; };
  }, [open]);

  const selectedSession = useMemo(
    () => sessions.find((s) => s.id === sessionId) || null,
    [sessions, sessionId],
  );

  // When session is picked, pre-fill capacity + duration with its defaults.
  useEffect(() => {
    if (selectedSession) {
      setMaxCapacity(String(selectedSession.max_capacity || 6));
      setDurationHours(String(selectedSession.default_duration_hours || 1));
    }
  }, [selectedSession]);

  const endTime = useMemo(() => {
    if (!startTime) return '';
    return addHours(startTime, parseFloat(durationHours || '1'));
  }, [startTime, durationHours]);

  // Pricing row matching current num_kids (capacity) + duration.
  const matchingPrice = useMemo(() => {
    if (!selectedSession) return null;
    const grid = coachPricing[selectedSession.coach_id] || [];
    const kids = Number(maxCapacity) || selectedSession.max_capacity || 0;
    const dur = parseInt(durationHours, 10);
    return grid.find((g) => g.num_kids === kids && g.duration_hours === dur) || null;
  }, [selectedSession, coachPricing, maxCapacity, durationHours]);

  const handleSubmit = async () => {
    if (!sessionId) return;
    setSubmitting(true);
    try {
      const res = await api.post('/small-group/slots', {
        session_id: sessionId,
        date,
        start_time: startTime,
        end_time: endTime,
        max_capacity: Number(maxCapacity) || undefined,
        lane_id: laneId,
      });
      toast.success(`Slot reserved on ${laneName || `Lane ${laneId}`} for "${selectedSession?.name}"`);
      onOpenChange(false);
      if (onCreated) onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reserve slot');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = sessionId && !submitting && !loadingSessions;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="create-small-group-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UsersRound className="h-4 w-4" /> Reserve Small Group Session
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <div className="font-medium text-slate-700">
              {laneName || `Lane ${laneId}`} · {date}
            </div>
            <div className="text-slate-500 text-xs">
              Starts at {formatTime(startTime || '')}
              {endTime && ` · ends at ${formatTime(endTime)}`}
            </div>
          </div>

          {loadingSessions ? (
            <div className="flex items-center justify-center py-6 text-slate-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading sessions…
            </div>
          ) : sessions.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="sg-no-sessions">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">No baseline sessions yet</p>
                <p className="text-xs mt-1">
                  An admin must first create a small-group session (with coach + pricing-per-hour) before
                  you can reserve a slot from the lane calendar.
                </p>
                <Link
                  to="/admin/small-group"
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline mt-2"
                  data-testid="sg-go-to-management-link"
                >
                  Open Small Group Management <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>Baseline session *</Label>
                <Select value={sessionId} onValueChange={setSessionId}>
                  <SelectTrigger data-testid="sg-session-select">
                    <SelectValue placeholder="Pick a session template" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessions.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`sg-session-${s.id}`}>
                        {s.name} · {s.coach_name || 'Coach TBD'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedSession && (
                <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs space-y-1" data-testid="sg-session-preview">
                  <div><span className="font-medium text-violet-900">Coach:</span> {selectedSession.coach_name || '—'}</div>
                  <div><span className="font-medium text-violet-900">Default capacity:</span> {selectedSession.max_capacity}</div>
                  {selectedSession.description && (
                    <div className="text-violet-800/80 italic">{selectedSession.description}</div>
                  )}
                  {coachPricing[selectedSession.coach_id]?.length ? (
                    <div className="pt-1.5">
                      <div className="font-medium text-violet-900 mb-1">Coach pricing (per child)</div>
                      <div className="max-h-24 overflow-y-auto">
                        <table className="w-full text-[11px]">
                          <thead className="text-violet-700">
                            <tr>
                              <th className="text-left font-medium">Kids</th>
                              <th className="text-left font-medium">Duration</th>
                              <th className="text-right font-medium">Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {coachPricing[selectedSession.coach_id].map((g, idx) => (
                              <tr key={idx} className={
                                matchingPrice && g.num_kids === matchingPrice.num_kids && g.duration_hours === matchingPrice.duration_hours
                                  ? 'bg-violet-200/60 font-medium'
                                  : ''
                              }>
                                <td>{g.num_kids}</td>
                                <td>{g.duration_hours}h</td>
                                <td className="text-right">{cs()}{Number(g.rate_per_child || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-violet-700/70 italic">No pricing grid configured for this coach yet.</div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Duration</Label>
                  <Select value={durationHours} onValueChange={setDurationHours}>
                    <SelectTrigger data-testid="sg-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sg-capacity">Max capacity</Label>
                  <Input
                    id="sg-capacity"
                    type="number"
                    min={1}
                    max={20}
                    value={maxCapacity}
                    onChange={(e) => setMaxCapacity(e.target.value)}
                    data-testid="sg-capacity-input"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-500">
                The lane will be blocked automatically for this slot. Manage registrations from{' '}
                <Link to="/admin/small-group" className="text-blue-700 hover:underline">Small Group Management</Link>.
              </p>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="sg-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="sg-submit-btn"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reserving…</> : 'Reserve slot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
